import React, { createContext, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import fakeShipmentData from '../../dataSets/fakeShipmentData';
import fakeClientData from '../../dataSets/fakeClientData';
import fakeCarrierData from '../../dataSets/fakeCarrierData';
import { useFilter } from '../FilterContext/FilterContext';
import { formatTextInput } from '../../utils/dataFormatting';
import {
  defaultShipmentData,
  defaultClientData,
  defaultCarrierData,
} from '../../dataSets/defaultFormData';
import { exitFullScreen } from '../../utils/fullScreenUtils';

const FormContext = createContext();

export function useForm() {
  const context = useContext(FormContext);
  if (!context) {
    console.error('useForm must be used within a BaseForm');
  }
  return context;
}

function BaseForm({ children, data_type }) {
  const navigate = useNavigate();
  const { filters } = useFilter();
  const location = useLocation();
  const urlPath = location.pathname;

  const [isNew, setIsNew] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const getDefaultData = () => {
    if (data_type === 'shipment') {
      return defaultShipmentData;
    } else if (data_type === 'client') {
      return defaultClientData;
    } else if (data_type === 'carrier') {
      return defaultCarrierData;
    }
  };
  const [formData, setFormData] = useState(getDefaultData());
  const [hasChanged, setHasChanged] = useState(false);
  const [messageBoxConfig, setMessageBoxConfig] = useState({
    message: '',
    type: 'ok',
    onConfirm: null,
    onCancel: () => setShowMessageBox(false),
    onClose: () => setShowMessageBox(false),
    loading_button: false,
    input: false,
  });
  const [showMessageBox, setShowMessageBox] = useState(false);

  const delay = ms => new Promise(res => setTimeout(res, ms));

  const field_names_translations = generate_field_names();

  function generate_field_names() {
    return {
      pickup_address1: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
      pickup_address2: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
      pickup_postcode: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
      pickup_city: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
      delivery_address1: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
      delivery_address2: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
      delivery_postcode: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
      delivery_city: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, ''),
    };
  }

  function translate_field_name(text) {
    for (const [key, val] of Object.entries(field_names_translations)) {
      if (val === text) {
        return key;
      }
    }
    return text;
  }

  const handleChange = e => {
    const { type, checked, value } = e.target;
    const name = translate_field_name(e.target.name);
    const isCheckbox = type === 'checkbox';
    const isRadio = type === 'radio';
    const isSelect = e.target.tagName === 'SELECT';

    if (!isLoading) {
      setHasChanged(true);
    }

    let filteredValue = value || '';
    if (!isCheckbox && !isRadio && !isSelect) {
      filteredValue = formatTextInput(filteredValue);
    }
    const newValue = isCheckbox
      ? checked
      : isRadio
        ? value === 'true'
          ? true
          : value === 'false'
            ? false
            : value
        : filteredValue;

    setFormData({
      ...formData,
      [name]: newValue,
    });
  };

  const handleBack = () => {
    if (hasChanged) {
      setMessageBoxConfig({
        message: `Revenir au tableau ? Les modifications non sauvegardées seront perdues.`,
        type: 'yesno',
        loading_button: false,
        input: false,
        onConfirm: handleConfirmBack,
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    } else {
      handleConfirmBack();
    }
  };

  const handleConfirmBack = () => {
    // Utiliser `filters` pour reconstruire les searchParams
    exitFullScreen();
    if (urlPath.includes('/dossiers/')) {
      navigate(`/dossiers/?${filters}`);
    } else if (urlPath.includes('/pre-fac/')) {
      navigate(`/pre-fac/?${filters}`);
    } else if (urlPath.includes('/supprimes/')) {
      navigate(`/supprimes/?${filters}`);
    } else if (urlPath.includes('/clients/')) {
      navigate(`/clients/?${filters}`);
    } else if (urlPath.includes('/transporteurs/')) {
      navigate(`/transporteurs/?${filters}`);
    }
  };

  const handleFakeFormClick = () => {
    // Utiliser `filters` pour reconstruire les searchParams
    let fakeData = {};
    if (data_type === 'shipment') {
      fakeData = fakeShipmentData;
    } else if (data_type === 'client') {
      fakeData = fakeClientData;
    } else if (data_type === 'carrier') {
      fakeData = fakeCarrierData;
    }
    setFormData(prevFormData => ({
      ...prevFormData,
      ...fakeData,
    }));
  };

  return (
    <FormContext.Provider
      value={{
        filters,
        isLoading,
        setIsLoading,
        isNew,
        setIsNew,
        formData,
        setFormData,
        messageBoxConfig,
        setMessageBoxConfig,
        showMessageBox,
        setShowMessageBox,
        handleChange,
        handleBack,
        handleConfirmBack,
        handleFakeFormClick,
        field_names_translations,
        delay,
        hasChanged,
        setHasChanged,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}

export default BaseForm;
